<!--
  The page for viewing and editing entities
-->

<template>
  <el-form
    ref="form"
    v-loading="state.loading"
    :model="viewObj"
    label-width="120px"
    :hide-required-asterisk="true">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home', query: {view: view, compare: compare} }">
          Home
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-if="skill==='global'">
        <router-link :to="{ name: 'global', query: {view: view, compare: compare} }">
          Global items
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-else>
        <router-link :to="{ name: 'skill', params: {skill: skill}, query: {view: view, compare: compare} }">
          Skill summary
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>{{entityId==='new'?'Create entity':'Entity details'}}</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title
      :skill="skill"
      v-model="viewObj"
      id-field="entity"
      :object-id="entityId"
      :disabled="viewObj.source!=='dict'"
      :existing="skillObj.data.entities">
      Entity: {{viewObj.name}}
    </main-title>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :object="viewObj"
      :publishable="skill === 'global'"
      :discrete="skill !== 'global' && skillObj.data.discrete_publish"
      :new-item="entityId==='new' || special && !timestamp"
      name="entity"
      @save="save"
      @delete="remove"
      @test="test"
      @untest="untest"
      @publish="publish"
      @unpublish="unpublish"
      @unprod="unprod"
      @reject="reject"
      @approve="approve"
      @disapprove="disapprove"
      @restore="restore"
      ></view-selector>
    <p>
      <subtitle>Entity</subtitle>
      <!-- only dict-type can be managed -->
      <el-alert
        v-if="viewObj.source!=='dict'"
        type="warning"
        show-icon
        :closable="false">
        <template slot="title">Unmanageable <em>entity</em> type</template>
      </el-alert>
      <!-- name input -->
      <comparator :view="viewObj" :compare="compareObj"
        prop="name"
        :required="true"
        :rules="AcceptableValidator">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            :prop="prop"
            v-bind="attrs"
            label="Name">
            <el-input v-model="value.name"
              :disabled="compare || special || viewObj.source!=='dict'"></el-input>
          </el-form-item>
        </template>
      </comparator>
      <!-- collapsable for advanced option -->
      <el-collapsable v-if="!special"
        v-model="advanced"
        :forced="viewObj.custom">
        <!-- expected languge select -->
        <comparator :view="viewObj" :compare="compareObj"
          prop="custom"
          :default="null">
          <template v-slot="{ value, compare, prop, attrs, status }">
            <el-form-item :class="'comparable-' + status"
              :prop="prop"
              v-bind="attrs">
              <template slot="label">
                Custom entity
                <tooltip-icon placement="bottom">
                  If the <em>entity</em> is custom and its content will depends on the user.
                </tooltip-icon>
              </template>
              <el-checkbox
              v-model="value.custom"
              :disabled="compare"></el-checkbox>
            </el-form-item>
          </template>
        </comparator>
      </el-collapsable>
    </p>
    <!-- content editor for dict-type entities -->
    <p>
      <subtitle>Values
        <template slot="tooltip">
          Configure values, and all the words that are translated to each value.<br/>
          Values can be of any type: <code>"myValue"</code>, <code>123</code>, <code>[1,2,3]</code>
        </template>
      </subtitle>
      <comparator :view="viewObj" :compare="compareObj"
        prop="source">
        <template v-slot="{ value, other, view, status}">
          <entity-dict-editor v-if="value.source==='dict'"
            :prop="view && 'options'"
            v-model="value.options"
            :other="other && other.options">
          </entity-dict-editor>
        </template>
      </comparator>
    </p>
  </el-form>
</template>

<script>
import State from '../../stores/state.js'
import ViewSelector from '../ViewSelector.vue'
import { AcceptableValidator } from '../../utils/validators'
import EntityDictEditor from './EntityDictEditor.vue'
import ElCollapsable from '../element/ElCollapsable.vue'

export default {
  name: 'EntityPage',
  components: {
    ViewSelector,
    EntityDictEditor,
    ElCollapsable,
  },
  props: {
    skill: String,
    entity: String, // 'new' to create a new entity
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      entityId: null, // also updated once the entity is created
      skillObj: State.empty,
      viewObj: {
        source: 'dict',
        name: '',
        options: {},
      },
      compareObj: undefined,
      advanced: false,
      AcceptableValidator,
    }
  },
  inject: ['onerror'],
  // load skill and entity
  created () {
    this.entityId = this.entity
    Promise.all([
      this.loadSkill(),
      this.loadView(),
      this.loadCompare(),
    ]).finally(() => {
      this.$anchorsLoaded()
    })
  },
  computed: {
    // shortcuts for useful properties
    status () { return this.viewObj.status || null },
    timestamp () { return this.viewObj.timestamp || null },
    published () { return this.viewObj.published || null },
    approved () { return this.viewObj.approved || null },
    special () {
      return this.viewObj.entity
        ? this.viewObj.entity.length !== 32 : false
    },
  },
  watch: {
    // user connected, reload everything
    'state.user' () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // skill changed, reaload everything
    skill () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // entity changed, reaload entity data
    entity () {
      if (this.entityId === this.entity) return
      this.entityId = this.entity
      this.loadView()
      this.loadCompare()
    },
    // view changed, reload view skill and entity
    view () {
      this.loadSkill()
      this.loadView()
    },
    // view compare, reload compare entity
    compare () { this.loadCompare() },
  },
  methods: {
    // load skill data
    loadSkill () {
      let skill = this.skill, view = this.view
      return State.getSkill(skill, view).then(data => {
        if (skill !== this.skill || view !== this.view) return
        this.skillObj = data
      }).catch(this.onerror)
    },
    // load entity data
    loadView () {
      if (this.entityId === 'new') {
        this.viewObj = {
          source: 'dict',
          name: '',
          options: {},
        }
        return Promise.resolve()
      } else {
        let skill = this.skill, entity = this.entityId, view = this.view
        return State.getEntity(skill, entity, view).then(data => {
          if (skill !== this.skill || entity !== this.entityId ||
            view !== this.view) return
          this.viewObj = data
        }).catch(onerror)
      }
    },
    // load compare entity data
    loadCompare () {
      if (this.entityId === 'new' || !this.compare) {
        this.compareObj = undefined
        return Promise.resolve()
      } else {
        let skill = this.skill, entity = this.entityId, view = this.compare
        return State.getEntity(skill, entity, view).then(data => {
          if (skill !== this.skill || entity !== this.entityId ||
            view !== this.compare) return
          this.compareObj = data
        }).catch(onerror)
      }
    },
    // delete entity
    remove () {
      State.removeEntity(this.skill, this.entityId).then(() => {
        this.$router.replace('/skills/' + this.skill +
          '/entities/new')
      }).catch(this.onerror)
    },
    // save entity
    save () {
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          let promise
          if (this.entityId === 'new') {
            promise = State.createEntity(this.skill, this.viewObj, this.viewObj.entity)
              .then(data => {
                this.$message({
                  message: 'Entity created',
                  type: 'success',
                })
                this.entityId = data.entity
                this.viewObj = data
                this.$router.replace({
                  name: 'entity',
                  params: { skill: this.skill, entity: this.entityId },
                })
              })
          } else {
            promise = State.saveEntity(this.skill, this.entityId, this.viewObj)
              .then(data => {
                this.$message({
                  message: 'Entity saved',
                  type: 'success',
                })
                this.viewObj = data
              })
          }
          promise.then(() => {
            return State.getSkill(this.skill, null, true)
          }).catch(this.onerror)
        } else {
          console.error(errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // remove entity from test cache
    untest () {
      State.untestEntity(this.skill, this.entityId).then(() => {
        this.$message({
          message: 'Test cleared',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // test entity
    test () {
      State.testEntity(this.skill, this.entityId).then(() => {
        this.$message({
          message: 'Entity in test',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // update entityObj fields after publishing
    updateEntity (data) {
      if (!data || !data.entity) return
      for (let field in data) this.viewObj[field] = data[field]
    },
    // remove entity from published cache
    unpublish () {
      State.unpublishEntity(this.skill, this.entityId, this.published).then(data => {
        this.updateEntity(data)
        this.$message({
          message: 'Entity unpublished',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // remove entity from approved cache
    unprod () {
      State.publishEntity(this.skill, this.entityId, false).then(data => {
        this.updateEntity(data)
        this.$message({
          message: 'Entity unproded',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // publish entity
    publish () {
      State.publishEntity(this.skill, this.entityId, this.timestamp).then(data => {
        this.updateEntity(data)
        this.$message({
          message: 'Entity published',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // disapprove entity
    disapprove () {
      State.disapproveEntity(this.skill, this.entityId, this.approved).then(data => {
        this.updateEntity(data)
        this.$message({
          message: 'Entity disapproved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // reject entity
    reject () {
      State.rejectEntity(this.skill, this.entityId, this.published).then(data => {
        this.updateEntity(data)
        this.$message({
          message: 'Entity rejected',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // approve entity
    approve () {
      State.approveEntity(this.skill, this.entityId, this.published).then(data => {
        this.updateEntity(data)
        this.$message({
          message: data.published ? 'Entity approved' : 'Deletion approved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // restore published or approved entity to dev
    restore (version) {
      let timestamp = version === 'pub' ? this.published : this.approved
      let name = version === 'pub' ? 'Published' : 'Approved'
      console.log('restore', version, timestamp, name)
      State['restore' + name + 'Entity'](this.skill, this.entityId, timestamp).then(() => {
        this.$message({
          message: name + ' entity restored',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // change view or compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
  },
}
</script>
